import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import CrpReportService from '../../../../shared/services/crp-report/crp-report.service'
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service'
import {
  CrpPlanType,
  CrpRegion,
  CrpScope3Coverage,
  ICrpFigures,
  ICrpReport,
} from '../../../../shared/services/crp-report/crp-report.model'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../../loading-page'
import { ImportStepHeader } from '../../settings/data-import/import-data/steps/import-step-header'
import { CrpStepPeriods } from '../../../presentation-components/reporting-components/crp/crp-step-periods'
import { CrpStepEmissionsReview } from '../../../presentation-components/reporting-components/crp/crp-step-emissions-review'
import { CrpStepNarratives } from '../../../presentation-components/reporting-components/crp/crp-step-narratives'
import { CrpStepSignOff } from '../../../presentation-components/reporting-components/crp/crp-step-signoff'
import { CrpStepGenerate } from '../../../presentation-components/reporting-components/crp/crp-step-generate'

const STEPS = ['Years and basis', 'Review emissions', 'Targets and projects', 'Declaration', 'Generate']

/** Narrative and sign-off fields the wizard edits locally and saves on Next. */
const DRAFT_FIELDS = [
  'commitmentText',
  'targetsNarrative',
  'completedMeasuresNarrative',
  'plannedMeasuresNarrative',
  'baselineAdditionalDetails',
  'currentAdditionalDetails',
  'progressNarrative',
  'signOffName',
  'signOffTitle',
  'signOffDate',
  'publicationDate',
]

/**
 * The Carbon Reduction Plan wizard. Serves both /new (before a plan row exists) and /:id/edit.
 * Viewing a generated plan is the detail page's job (ReportingCrpDetailPage); this page only edits
 * and generates.
 *
 * Step 1 creates the plan on Next, because everything after it needs a saved plan to compute figures
 * against. Later steps save their edits on Next so a half-finished plan survives a refresh.
 */
export const ReportingCrpWizardPage = () => {
  const crpReportService = useMemo(() => new CrpReportService(), [])
  const reportPeriodService = useMemo(() => new ReportPeriodService(), [])
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)

  const [report, setReport] = useState<ICrpReport | null>(null)
  const [figures, setFigures] = useState<ICrpFigures | null>(null)
  // When the figures cannot be computed (for example a base year with no figures recorded for the
  // chosen basis), the review step would otherwise sit on a spinner with only a transient toast.
  // The message is held here, shown as a banner, and the user is returned to step 1 to act on it.
  const [figuresError, setFiguresError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, any>>({})
  const [activeStep, setActiveStep] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  // The plan's artefacts are produced by the worker after the generate call returns, so the
  // button must stay held down until the tracked downloads finish, not just until the API
  // responds. Otherwise Generate re-arms mid-generation and a second click queues a second run.
  const [artefactDownloadIds, setArtefactDownloadIds] = useState<string[]>([])

  // Step 1 selections, held locally until the plan row exists.
  const [baselineReportPeriodId, setBaselineReportPeriodId] = useState('')
  const [currentReportPeriodId, setCurrentReportPeriodId] = useState('')
  const [region, setRegion] = useState<CrpRegion>('uk')
  const [scope3Coverage, setScope3Coverage] = useState<CrpScope3Coverage>('PPN_MINIMUM')
  const [planType, setPlanType] = useState<CrpPlanType>('CRP')

  const reportPeriods = useMemo(
    () => [...(reportPeriodContext.allReportPeriods || [])]
      .sort((a, b) => Number(b.calendarYear) - Number(a.calendarYear)),
    [reportPeriodContext.allReportPeriods],
  )

  // Baseline candidates include ARCHIVED report periods: a manually entered base year is the
  // normal baseline for an organisation whose reference year predates their recorded data. The
  // current reporting year stays a real report period, so it keeps using the context list above.
  const [baselinePeriods, setBaselinePeriods] = useState<any[]>([])
  useEffect(() => {
    let cancelled = false
    reportPeriodService.getReportPeriodAndArchivedReportPeriodsWithPagination(0, 200)
      .then((response) => {
        if (cancelled) return
        const rows = [...(response.rows || [])]
          .sort((a: any, b: any) => Number(b.calendarYear) - Number(a.calendarYear))
        setBaselinePeriods(rows)
      })
      .catch((err) => console.log(err))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showError = useCallback((message: string) => {
    userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
  }, [userNotificationsContext])

  // Default the baseline to the period the customer has already marked as their base year, wherever
  // it lives (real or archived): the CRP's baseline should be the same fixed reference the rest of
  // the platform reduces against.
  useEffect(() => {
    if (id || baselineReportPeriodId || !reportPeriods.length || !baselinePeriods.length) return
    const baseYearPeriod = baselinePeriods.find((period: any) => period.baseYear)
    setBaselineReportPeriodId(baseYearPeriod?.id || baselinePeriods[baselinePeriods.length - 1]?.id || '')
    setCurrentReportPeriodId(reportPeriods[0]?.id || '')
  }, [id, reportPeriods, baselinePeriods, baselineReportPeriodId])

  useEffect(() => {
    if (!id) {
      setIsLoading(false)
      return
    }
    crpReportService.getOne(id)
      .then((response) => {
        applyReport(response)
        const requestedStep = Number(searchParams.get('step'))
        setActiveStep(Number.isFinite(requestedStep) && requestedStep > 0
          ? Math.min(requestedStep, STEPS.length - 1)
          : 0)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        showError('Could not load this Carbon Reduction Plan')
      })
    // Re-reads only when the plan id changes; step is applied from the URL on that load.
  }, [id])

  // The steps are long enough to scroll, so moving between them would otherwise drop the user
  // part-way down the next step with the heading off screen.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const loadFigures = useCallback(async (reportId: string) => {
    try {
      setFiguresError(null)
      setFigures(await crpReportService.getFigures(reportId))
    } catch (err: any) {
      console.log(err)
      // Show the reason on screen and return to the first step: the fixes it asks for (add figures
      // to the base year, or change the basis) both live there or outside the wizard, and the later
      // steps cannot render without figures.
      setFiguresError(err?.response?.data?.message || 'Could not calculate your emissions figures. Please try again.')
      setActiveStep(0)
      if (id) setSearchParams({ step: '0' }, { replace: true })
    }
  }, [crpReportService, id, setSearchParams])

  // Figures are needed from the review step onward, and are recomputed when the basis changes or the
  // plan is generated (which switches the response from live figures to the frozen snapshot).
  const needsFigures = activeStep >= 1
  useEffect(() => {
    if (!report || !needsFigures) return
    loadFigures(report.id)
  }, [report?.id, report?.region, report?.scope3Coverage, report?.status, report?.generatedAt, needsFigures])

  const applyReport = (response: ICrpReport) => {
    setReport(response)
    // The stored baseline is one of two columns: a real report period or an archived base year.
    setBaselineReportPeriodId(response.baselineReportPeriodId || response.baselineArchivedReportPeriodId || '')
    setCurrentReportPeriodId(response.currentReportPeriodId || '')
    setRegion(response.region)
    setScope3Coverage(response.scope3Coverage)
    setPlanType(response.planType)
    const next: Record<string, any> = {}
    for (const field of DRAFT_FIELDS) next[field] = (response as any)[field] ?? ''
    setDraft(next)
  }

  const step1Valid = Boolean(
    baselineReportPeriodId
    && (planType === 'PROGRESS' || (currentReportPeriodId && currentReportPeriodId !== baselineReportPeriodId)),
  )

  const handleStep1Change = (field: string, value: string) => {
    if (field === 'baselineReportPeriodId') setBaselineReportPeriodId(value)
    if (field === 'currentReportPeriodId') setCurrentReportPeriodId(value)
    if (field === 'region') setRegion(value as CrpRegion)
    if (field === 'scope3Coverage') setScope3Coverage(value as CrpScope3Coverage)
    if (field === 'planType') setPlanType(value as CrpPlanType)
  }

  const handleDraftChange = (field: string, value: any) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
  }

  const saveStep1 = async (): Promise<ICrpReport | null> => {
    const payload = {
      baselineReportPeriodId,
      currentReportPeriodId: planType === 'PROGRESS' ? null : currentReportPeriodId,
      region,
      scope3Coverage,
      planType,
    }
    if (!report) return crpReportService.create(payload)
    // Periods are fixed once generated; only the basis can still change.
    const updatable = report.status === 'GENERATED'
      ? { region, scope3Coverage }
      : payload
    return crpReportService.update(report.id, updatable as Partial<ICrpReport>)
  }

  /** Persists whatever the current step edits. Steps 2 and 5 have nothing of their own to save. */
  const saveCurrentStep = async (): Promise<ICrpReport | null> => {
    if (activeStep === 0) {
      const saved = await saveStep1()
      if (saved) applyReport(saved)
      return saved
    }
    if (report && (activeStep === 2 || activeStep === 3)) {
      const saved = await crpReportService.update(report.id, draft as Partial<ICrpReport>)
      applyReport(saved)
      return saved
    }
    return report
  }

  const handleNext = async () => {
    setIsSaving(true)
    try {
      const saved = await saveCurrentStep()
      // A new plan gets its own URL so a refresh (or the browser back button) returns to the draft
      // rather than starting over.
      if (activeStep === 0 && !id && saved) {
        navigate(`/reporting/carbon-reduction-plan/${saved.id}/edit?step=1`, { replace: true })
      }
      const nextStep = Math.min(activeStep + 1, STEPS.length - 1)
      setActiveStep(nextStep)
      if (id) setSearchParams({ step: String(nextStep) }, { replace: true })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'There was an error, please try again')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAndClose = async () => {
    setIsSaving(true)
    try {
      await saveCurrentStep()
      userNotificationsContext.openSnackbar({ open: true, message: 'Draft saved. Pick it up any time from the plans list.', backgroundColor: 'text.primary' })
      navigate('/reporting/carbon-reduction-plan')
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not save your draft, please try again')
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    const previousStep = Math.max(activeStep - 1, 0)
    setActiveStep(previousStep)
    if (id) setSearchParams({ step: String(previousStep) }, { replace: true })
  }

  const handleGenerate = async () => {
    if (!report) return
    setIsGenerating(true)
    try {
      const response = await crpReportService.generate(report.id)
      // applyReport changes status/generatedAt, which re-runs the figures effect so the step switches
      // from live figures to the frozen snapshot. Do NOT clear figures here: nothing would refetch
      // them if the plan was regenerated with an unchanged basis, leaving this step blank.
      applyReport(response.crpReport)
      // Each artefact is tracked separately so the user sees all three complete.
      setArtefactDownloadIds(response.fileDownloads.map((fileDownload: { id: string }) => fileDownload.id))
      for (const fileDownload of response.fileDownloads) {
        downloadStatusContext.startDownloadTracking(fileDownload.id)
      }
      userNotificationsContext.openSnackbar({ open: true, message: 'Generating your Carbon Reduction Plan', backgroundColor: 'text.primary' })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not generate your plan, please try again')
    } finally {
      setIsGenerating(false)
    }
  }

  const renderStepContent = (step: number) => {
    if (step === 0) {
      return (
        <CrpStepPeriods
          reportPeriods={reportPeriods}
          baselinePeriods={baselinePeriods}
          baselineReportPeriodId={baselineReportPeriodId}
          currentReportPeriodId={currentReportPeriodId}
          region={region}
          scope3Coverage={scope3Coverage}
          planType={planType}
          locked={report?.status === 'GENERATED'}
          onChange={handleStep1Change}
        />
      )
    }
    if (!report || !figures) {
      return <LoadingPage pageReady={false}><Box /></LoadingPage>
    }
    if (step === 1) return <CrpStepEmissionsReview figures={figures} />
    if (step === 2) {
      return (
        <CrpStepNarratives
          values={draft}
          isProgress={report.planType === 'PROGRESS'}
          figures={figures}
          onChange={handleDraftChange}
        />
      )
    }
    if (step === 3) return <CrpStepSignOff values={draft} figures={figures} onChange={handleDraftChange} />
    return (
      <CrpStepGenerate
        report={report}
        figures={figures}
        isGenerating={isGenerating || downloadStatusContext.downloads.some(d =>
          artefactDownloadIds.includes(d.id) && (d.status === 'PENDING' || d.status === 'IN_PROGRESS'))}
        onGenerate={handleGenerate}
        onViewPlan={() => navigate(`/reporting/carbon-reduction-plan/${report.id}`)}
      />
    )
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title={planType === 'PROGRESS' ? 'Carbon Reduction Progress Report' : 'Carbon Reduction Plan (PPN 006)'}
        includeBackButton={true}
        backButtonLink={report?.status === 'GENERATED'
          ? `/reporting/carbon-reduction-plan/${report.id}`
          : '/reporting/carbon-reduction-plan'}
        backButtonText={report?.status === 'GENERATED' ? 'Back to plan' : 'Back to plans'}
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Box sx={{ width: '100%', maxWidth: 880 }}>
            <ImportStepHeader currentStep={activeStep + 1} totalSteps={STEPS.length} />
            {figuresError && (
              <Alert severity='error' sx={{ mb: 3 }} onClose={() => setFiguresError(null)}>
                {figuresError}
              </Alert>
            )}
            <Box sx={{ mb: 4 }}>
              {renderStepContent(activeStep)}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant='outlined'
                onClick={handleBack}
                startIcon={<ChevronLeft />}
                disabled={activeStep === 0}
                sx={{ minWidth: 100, textTransform: 'none' }}
              >
                <Typography>Back</Typography>
              </Button>
              <Stack direction='row' spacing={1}>
                {/* A generated plan is no longer a draft, so it only offers Next (edits still save on
                    Next as before). */}
                {report?.status !== 'GENERATED' && activeStep < STEPS.length - 1 && (
                  <Button
                    variant='outlined'
                    onClick={handleSaveAndClose}
                    disabled={isSaving || (activeStep === 0 && !step1Valid)}
                    sx={{ textTransform: 'none' }}
                  >
                    <Typography>Save draft and close</Typography>
                  </Button>
                )}
                {activeStep < STEPS.length - 1 && (
                  <Button
                    variant='contained'
                    onClick={handleNext}
                    disabled={isSaving || (activeStep === 0 && !step1Valid) || (activeStep > 0 && !figures)}
                    endIcon={<ChevronRight />}
                    sx={{ minWidth: 100, textTransform: 'none' }}
                  >
                    <Typography>{isSaving ? 'Saving…' : 'Next'}</Typography>
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </LoadingPage>
    </PageOuterContainer>
  )
}
